/* eslint-disable react-hooks/exhaustive-deps */
import { DownloadOutlined, ExportOutlined } from '@ant-design/icons';
import { Button, Card, DatePicker, Flex, Modal, Space, Table, Tag, Typography } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { getLeadCheckinsReportDetails } from '../../../redux/actions/reports';
import { checkInTimeSpent, downloadFileDirectly, getDateRangeFormat } from '../../../utils';
import { Link } from 'react-router-dom';
import { getRoutes } from '../../../routes/Routes';
import { leadStages, leadType } from '../../../constants';

const { RangePicker } = DatePicker;

const CheckinsDetail = ({ filter, open, onClose, isDownload }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [localDates, setLocalDates] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        totalRecords: 0,
    });

    const activeDates = localDates || { start_date: filter?.start_date, end_date: filter?.end_date };

    const fetchData = async (page = 1) => {
        setLoading(true);
        try {
            const res = await getLeadCheckinsReportDetails({
                user_id: filter?.id || filter?.user_id,
                salesrep_netsuite_id: filter?.salesrep_netsuite_id,
                start_date: activeDates.start_date,
                end_date: activeDates.end_date,
                page,
            });
            setData(res?.leads || res?.check_ins || []);
            setPagination((prev) => ({
                ...prev,
                current: page,
                totalRecords: res?.total_count || 0,
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const res = await getLeadCheckinsReportDetails({
                user_id: filter?.id || filter?.user_id,
                start_date: activeDates.start_date,
                end_date: activeDates.end_date,
                download: 1,
            });
            if (res?.ack) {
                downloadFileDirectly(res?.file_url);
            }
        } finally {
            setDownloading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchData(1);
        }
    }, [open]);

    const handleTableChange = (paginationInfo) => {
        fetchData(paginationInfo.current);
    };

    const tableComponents = useMemo(() => ({
        body: {
            cell: ({ className, ...rest }) => (
                <td {...rest} className={data?.length ? clsx(className, 'py-1') : className} />
            ),
        },
    }), [data?.length]);

    const columns = [
        {
            title: 'Check In',
            dataIndex: 'check_in_time',
            width: 150,
            render: (_, record) => {
                if (!record.check_out_time) return;

                return <div className="check-status check-in">
                    <span className="dot" />
                    {record.check_in_time ? dayjs(record.check_in_time).format('DD-MM-YYYY HH:mm:ss') : '-'}
                </div>
            }
        },
        {
            title: 'Check Out',
            dataIndex: 'check_out_time',
            width: 150,
            render: (_, record) => {
                if (!record.check_out_time) return;

                return (
                    <div className="check-status check-out">
                        <span className="dot" />
                        {dayjs(record.check_out_time).format('DD-MM-YYYY HH:mm:ss')}
                    </div>
                );
            },
        },
        {
            title: 'Time Spent',
            key: 'time_spent',
            width: 120,
            render: (_, record) => (
                <span className="font-medium">{checkInTimeSpent(record)}</span>
            ),
        },
        {
            title: 'Shop Name',
            dataIndex: 'shop_name',
            key: 'shop_name',
            render: (text, record) => (
                <div className="inline-flex align-items-center gap-1 max-w-25rem">
                    <Typography.Text
                        ellipsis={{ tooltip: true, }}
                        className="m-0 max-w-full"
                    >
                        {text}
                    </Typography.Text>
                    {!!record?.lead_id && (
                        <Link
                            to={getRoutes().update_lead.route(
                                record?.lead_id
                            )}
                        >
                            <ExportOutlined />
                        </Link>
                    )}
                </div>
            ),
        },
        {
            title: 'Contact Person',
            dataIndex: 'contact_person',
            key: 'contact_person',
        },
        {
            title: 'Postcode',
            dataIndex: 'postcode',
            key: 'postcode',
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'Whatsapp',
            dataIndex: 'whatsapp',
            key: 'whatsapp',
        },
        {
            title: 'Pipeline Status/Stage',
            dataIndex: 'pipeline_stage',
            key: 'pipeline_stage',
            render: (text) => {
                const findStage = leadStages.find((stage) => stage.value === text);
                return findStage ? findStage.label : null;
            },
        },
        {
            title: 'Lead Type',
            dataIndex: 'type',
            key: 'type',
            render: (text) => {
                if (!text || !leadType[text]?.label) return null;
                return <Tag color={leadType[text]?.color}>{leadType[text]?.label}</Tag>;
            },
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
    ];

    return (
        <Modal
            title="Checkins Detail"
            open={open}
            onCancel={onClose}
            centered
            width="90vw"
            footer={null}
        >
            <Card size="small" className="mb-2 shadow-sm" variant="borderless">
                <Flex justify='space-between' wrap gap={16} align='center'>
                    <Space direction="vertical" size={0}>
                        <Typography.Text type="secondary">
                            Sales Rep
                        </Typography.Text>

                        <Typography.Text strong>
                            {filter?.salesrep_name}
                        </Typography.Text>
                    </Space>
                    <Flex wrap gap={8} align="center" className="w-full lg:w-auto lg:flex-1" justify="end">
                        <RangePicker
                            className="w-full lg:max-w-20rem"
                            format="DD-MM-YYYY"
                            size="large"
                            value={[
                                activeDates.start_date ? dayjs(activeDates.start_date) : null,
                                activeDates.end_date ? dayjs(activeDates.end_date) : null,
                            ]}
                            onChange={(dates) => {
                                const { start_date, end_date } = getDateRangeFormat(dates);
                                setLocalDates({ start_date, end_date });
                            }}
                            allowClear={false}
                        />
                        {isDownload && (
                            <Button
                                size="large"
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={handleDownload}
                                loading={downloading}
                                disabled={data.length === 0}
                            >
                                Download
                            </Button>
                        )}
                    </Flex>
                </Flex>
            </Card>

            <Table
                size="small"
                columns={columns}
                rowKey={(record) => record?.id || Math.random()}
                dataSource={data}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.totalRecords,
                    showSizeChanger: false,
                    responsive: true,
                    size: 'small',
                    showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} Records`,
                }}
                onChange={handleTableChange}
                loading={loading}
                scroll={{ x: 'max-content', y: '65vh' }}
                components={tableComponents}
            />
        </Modal>
    );
};

export default CheckinsDetail;